import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { List } from "@/components/list/List";
import AsyncButton from "@/components/AsyncButton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { displayProjectStore } from "@/schemas/projectStore";
import { DisplayProjectStore } from "@/types/projectStore";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import StoreGroupFilter from "./StoreGroupFilter";
import { useProjectStoreContext } from "./ProjectStoreContext";
import { useProjectStoresQuery } from "@/modules/projects/hooks/useProjectStoresQuery";
import { FilterType } from "@/lib/filters";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useCheckIsProjectLocked } from "../hooks/useCheckIsProjectLocked";
import { useProjectStoresAdd } from "../hooks/useProjectStoresAdd";
import { Row } from "@tanstack/react-table";
import { Save } from "lucide-react";
import { useRowSelectionCount } from "@/components/data-table/hooks/useRowSelectionCount";

const ProjectStoreSubmitButton = ({
  onSubmit,
  isSubmitting,
  disabled,
}: {
  onSubmit: () => void;
  isSubmitting: boolean;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  const count = useRowSelectionCount();

  return (
    <AsyncButton
      onClick={onSubmit}
      disabled={disabled || count === 0}
      loading={isSubmitting}
      label={t("projectStores.dialog.save")}
      loadingLabel={t("commonEditor.toolbar.saving")}
      icon={<Save className="mr-2 h-5 w-5" />}
    />
  );
};

const ProjectStoreCreatorContent = ({ onClose }: { onClose: () => void }) => {
  const { projectId } = useProjectStoreContext();
  const { t } = useTranslation();
  const { can_update } = useModulePermissions();
  const isProjectLocked = useCheckIsProjectLocked();
  const name = "projectStores";

  const { query, options } = useProjectStoresQuery({
    name,
    projectId,
    enabled: true,
    filters: [
      {
        id: "project_id",
        value: {
          value: projectId,
          filter: FilterType.IsEmpty,
        },
      },
    ],
  });

  const { store } = useRowSelectionContext();

  const handleSuccess = useCallback(() => {
    store.clear();
    onClose();
  }, [store, onClose]);

  const { handleSubmit, isSubmitting } = useProjectStoresAdd({
    query,
    projectId,
    onSuccess: handleSuccess,
  });

  const onSubmit = useCallback(() => {
    handleSubmit(Array.from(store.getSelection()));
  }, [handleSubmit, store]);

  const tableFeatures = useMemo(
    () => ({
      enableRowSelection: true,
      enableSelectAll: true,
    }),
    [],
  );

  const columnOverrides = useMemo(
    () => ({
      status: {
        id: "status",
        cell: ({ row }: { row: Row<DisplayProjectStore> }) =>
          t(`storeStatuses.${row.original.status}`),
      },
    }),
    [t],
  );

  const disabled = !can_update || isProjectLocked;

  return (
    <DataTableQueryContextProvider value={{ query }}>
      <List<DisplayProjectStore>
        schema={displayProjectStore}
        name={name}
        query={query}
        options={options}
        tableFeatures={tableFeatures}
        columnOverrides={columnOverrides}
      >
        <div className="flex flex-col gap-3 min-h-0 min-w-0">
          <DialogHeader>
            <DialogTitle>{t("projectStores.dialog.title")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-row items-center gap-2">
            <List.Menubar className="m-0 grow">
              <List.Menubar.Tools inDialog />
              <List.Menubar.Columns inDialog />
              <List.Menubar.Reset inDialog />
            </List.Menubar>
            <StoreGroupFilter inDialog className="grow-0 shrink-0" />
          </div>
          <div className="flex flex-row grow gap-2 overflow-hidden">
            <List.Content className="flex-grow min-w-0" isInDialog>
              <List.Status>
                <List.SelectedRowsStatus />
              </List.Status>
            </List.Content>
          </div>
          <DialogFooter className="gap-3">
            <DialogClose asChild>
              <Button variant="link" disabled={isSubmitting}>
                {t("projectStores.dialog.cancel")}
              </Button>
            </DialogClose>
            <ProjectStoreSubmitButton
              onSubmit={onSubmit}
              isSubmitting={isSubmitting}
              disabled={disabled}
            />
          </DialogFooter>
        </div>
      </List>
    </DataTableQueryContextProvider>
  );
};

const ProjectStoreCreator = () => {
  const { isAddLimitsDialogOpen, setIsAddLimitsDialogOpen } =
    useProjectStoreContext();

  const onOpenChange = useCallback(
    (open: boolean) => {
      setIsAddLimitsDialogOpen(open);
    },
    [setIsAddLimitsDialogOpen],
  );

  const onClose = useCallback(() => {
    setIsAddLimitsDialogOpen(false);
  }, [setIsAddLimitsDialogOpen]);

  return (
    <Dialog open={isAddLimitsDialogOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90dvw] h-[95dvh]">
        <RowSelectionProvider>
          <ProjectStoreCreatorContent onClose={onClose} />
        </RowSelectionProvider>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectStoreCreator;
